import { useEffect, useState, useRef } from "react";

export default function useNearScreen({
  distance = "100px",
  externalRef,
  scrollRootRef,
  once = true,
  disabledTimeout = false,
} = {}) {
  const [isNearScreen, setIsNearScreen] = useState(false);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const fromRef = useRef();
  const timeRef = useRef();

  useEffect(() => {
    let observer;
    let cancelled = false;

    const element = externalRef ? externalRef.current : fromRef.current;
    const root = scrollRootRef?.current ?? null;

    const onChange = (entries, observer) => {
      const el = entries[0];
      if (!el) return;
      if (el.isIntersecting) {
        setIsNearScreen(true);
        once && observer.disconnect();
      } else {
        !once && setIsNearScreen(false);
      }
    };

    Promise.resolve(
      typeof IntersectionObserver !== "undefined"
        ? IntersectionObserver
        : import("intersection-observer")
    ).then(() => {
      if (cancelled) return;
      observer = new IntersectionObserver(onChange, {
        root,
        rootMargin: distance,
      });

      if (element) observer.observe(element);
    });

    return () => {
      cancelled = true;
      observer && observer.disconnect();
    };
  });

  useEffect(() => {
    if (disabledTimeout) return;
    if (isNearScreen && !loadLastItem) {
      timeRef.current = setTimeout(() => {
        setIsNearScreen((prev) => {
          if (prev) {
            return false;
          }
          return prev;
        });
      }, 1000);
    }
    // eslint-disable-next-line
  }, [isNearScreen]);

  return {
    isNearScreen,
    fromRef,
    loadLastItem,
    onChangeLoadLastItem: setLoadLastItem,
  };
}
